import React, { Suspense, lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute"; // 引入刚才创建的组件
import { VERSION_UPDATE_EVENT } from "@/components/feature/VersionUpdateModal";

// 全局加载占位组件
// eslint-disable-next-line react-refresh/only-export-components
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    加载中...
  </div>
);

// 懒加载包装：chunk 加载失败（新版本发布后旧 chunk 被替换）时派发版本更新事件，
// 由全局弹框提示用户确认后刷新页面；只派发一次，避免多个失败导入重复弹窗，
// 并返回永不 resolve 的 Promise 避免 Suspense 报错。
let versionUpdateNotified = false;
const lazyWithReload = (importFn: () => Promise<{ default: React.ComponentType<any> }>) =>
  lazy(() =>
    importFn().catch((err) => {
      console.error('页面资源加载失败:', err);
      if (!versionUpdateNotified) {
        versionUpdateNotified = true;
        window.dispatchEvent(new Event(VERSION_UPDATE_EVENT));
      }
      // 保持 pending，等待用户确认刷新，避免 Suspense 报错
      return new Promise(() => {});
    })
  );

// --- 1. 懒加载公共页面 ---
const NotFound = lazyWithReload(() => import("../pages/NotFound"));
const Home = lazyWithReload(() => import("../pages/home/page"));
const About = lazyWithReload(() => import("../pages/about/page"));
const Capabilities = lazyWithReload(() => import("../pages/capabilities/page"));
const Products = lazyWithReload(() => import("../pages/products/page"));
const Partners = lazyWithReload(() => import("../pages/partners/page"));
const Contact = lazyWithReload(() => import("../pages/contact/page"));
const Admin = lazyWithReload(() => import("../pages/admin/page"));
const Login = lazyWithReload(() => import("../pages/login/page"));
const ForgotPassword = lazyWithReload(() => import("../pages/forgot-password/page"));

// --- 2. 懒加载需要权限的 Platform 页面 ---
const Platform = lazyWithReload(() => import("../pages/platform/page"));
const ContentAgent = lazyWithReload(() => import("../pages/platform/content-agent/page"));
const GeoAgent = lazyWithReload(() => import("../pages/platform/geo-agent/page"));
const CsAgent = lazyWithReload(() => import("../pages/platform/cs-agent/page"));
const ContentDetail = lazyWithReload(() => import("../pages/platform/content-detail/page"));
const ContentLibrary = lazyWithReload(() => import("../pages/platform/library/page"));
const MaterialLibrary = lazyWithReload(() => import("../pages/platform/material-library/page"));
const ProductManagement = lazyWithReload(() => import("../pages/platform/products/page"));
const ProductDetail = lazyWithReload(() => import("../pages/platform/products/detail/page"));
const Analytics = lazyWithReload(() => import("../pages/platform/analytics/page"));
const Settings = lazyWithReload(() => import("../pages/platform/settings/page"));
const ContentHub = lazyWithReload(() => import("../pages/platform/content-hub/page"));
const VisualStudio = lazyWithReload(() => import("../pages/platform/visual-studio/page"));
const AiImage = lazyWithReload(() => import("../pages/platform/ai-image/page"));
const FigureTraining = lazyWithReload(() => import("../pages/platform/figure-training/page"));
// 旧静态发布页已冻结，路由重定向到 publish-auto（组件文件保留但不再引用）
// const PublishAuto = lazyWithReload(() => import("../pages/platform/publish-auto/page"));
const PublishAuto = lazyWithReload(() => import("../pages/platform/publish/page"));
const DhAudit = lazyWithReload(() => import("../pages/platform/admin/dh-audit/page"));
const DhAccount = lazyWithReload(() => import("../pages/platform/admin/dh-account/page"));
const LlmChannels = lazyWithReload(() => import("../pages/platform/llm-channels/page"));
const routes: RouteObject[] = [
  // --- 公共路由 (无需登录) ---
  {
    path: "/",
    element: <Suspense fallback={<LoadingFallback />}><Home /></Suspense>,
  },
  {
    path: "/about",
    element: <Suspense fallback={<LoadingFallback />}><About /></Suspense>,
  },
  {
    path: "/capabilities",
    element: <Suspense fallback={<LoadingFallback />}><Capabilities /></Suspense>,
  },
  {
    path: "/products",
    element: <Suspense fallback={<LoadingFallback />}><Products /></Suspense>,
  },
  {
    path: "/partners",
    element: <Suspense fallback={<LoadingFallback />}><Partners /></Suspense>,
  },
  {
    path: "/contact",
    element: <Suspense fallback={<LoadingFallback />}><Contact /></Suspense>,
  },
  {
    path: "/admin",
    element: <Suspense fallback={<LoadingFallback />}><Admin /></Suspense>,
  },
  {
    path: "/login",
    element: <Suspense fallback={<LoadingFallback />}><Login /></Suspense>,
  },
  {
    path: "/forgot-password",
    element: <Suspense fallback={<LoadingFallback />}><ForgotPassword /></Suspense>,
  },

  // --- 受保护的路由 (需要登录) ---
  {
    path: "/platform",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <Outlet /> {/* 使用 Outlet 渲染子路由 */}
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true, // 对应 /platform 默认路由
        element: <Platform />,
      },
      {
        path: "content-agent",
        element: <ContentAgent />,
      },
      {
        path: "geo-agent",
        element: <GeoAgent />,
      },
      {
        path: "cs-agent",
        element: <CsAgent />,
      },
      {
        path: "content-hub",
        element: <ContentHub />,
      },
      {
        path: "visual-studio",
        element: <VisualStudio />,
      },
      {
        path: "ai-image",
        element: <AiImage />,
      },
      {
        // 克隆声音已并入数字人页面第一个页签，重定向兼容旧链接
        path: "voice-clone",
        element: <Navigate to="/platform/figure-training?tab=voice" replace />,
      },
      {
        path: "figure-training",
        element: <FigureTraining />,
      },
      {
        // 旧发布页入口：重定向到新版发布页，兼容外部收藏旧链接
        path: "publish",
        element: <Navigate to="/platform/publish-auto" replace />,
      },
      {
        path: "publish-auto",
        element: <PublishAuto />,
      },
      {
        // 兼容旧链接 publish1
        path: "publish1",
        element: <Navigate to="/platform/publish-auto" replace />,
      },
      {
        path: "content-detail",
        element: <ContentDetail />,
      },
      {
        path: "library",
        element: <ContentLibrary />,
      },
      {
        path: "material-library",
        element: <MaterialLibrary />,
      },
      {
        path: "products",
        element: <ProductManagement />,
      },
      {
        path: "products/:id",
        element: <ProductDetail />,
      },
      {
        path: "analytics",
        element: <Analytics />,
      },
      {
        path: "settings",
        element: <Settings />,
      },
      {
        // 数字人授权审核（超级管理员）
        path: "admin/dh-audit",
        element: <DhAudit />,
      },
      {
        // 数字人子账号管理（超级管理员）
        path: "admin/dh-account",
        element: <DhAccount />,
      },
      {
        // 渠道与模型（超级管理员）
        path: "llm-channels",
        element: <LlmChannels />,
      },
    ],
  },

  // --- 404 兜底路由 ---
  {
    path: "*",
    element: <Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>,
  },
];

export default routes;